import { IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact, useIonAlert, useIonRouter } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route } from 'react-router-dom';
import { Storage } from '@ionic/storage';
import { Device } from '@capacitor/device';
import { registerPlugin, Capacitor } from '@capacitor/core'
import { BackgroundGeolocationPlugin } from "@capacitor-community/background-geolocation";
import { App as CapacitorApp } from '@capacitor/app';
import React, { useContext, useEffect, useState } from 'react';
import Menu from './components/Menu';
import Home from './pages/Home';
import Docks from './pages/Docks';
import DocksMap from './pages/DocksMap';
import Dock from './pages/Dock';
import Maps from './pages/Maps';
import Islands from './pages/Islands';
import IslandsMap from './pages/IslandsMap';
import Routes from './pages/Routes';
import Batimetric from './pages/Batimetric';
import Signs from './pages/Signs';
import Bridges from './pages/Bridges';
import Rulebook from './pages/Rulebook';
import Feedback from './pages/Feedback';
import Credits from './pages/Credits';
import { LanguageProvider } from './contexts/LanguageContext';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
// global custom style 
import './theme/globalStyle.css';
/* array of vertices of the polygon defyning the relevant area around the Po river */
import { shapeVertices } from "./js/asta_po.js";
import { BASE_URL } from './utils/constant';
import Meatori from './pages/Meatori';
import Login from './pages/Login';
import { ActionType, AuthContext } from './context/AuthContext';
// import {shapeVertices} from "./js/asta_po_wide.js";
import { PushNotificationSchema, PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { KeepAwake } from '@capacitor-community/keep-awake';
import NavigationWarnings from './pages/NavigationWarnings';
import dangerZoneMonitor, { DANGER_ZONE_ALERT_EVENT, CriticalAlerts } from './services/DangerZoneMonitorService';
import { startAlarm, stopAlarm, initAlarmAudioUnlock } from './utils/alarmSound';

setupIonicReact();

// local storage
const localStore = new Storage();
let deviceId = "-";
const appVersion = "1.13";
const BackgroundGeolocation = Capacitor.getPlatform() === 'web' ? null : registerPlugin<BackgroundGeolocationPlugin>("BackgroundGeolocation");

/** Set by IonRouterBridge (must run inside IonReactRouter). */
let appIonRouter: ReturnType<typeof useIonRouter> | null = null;

function navigateAppRoute(path: string) {
  if (appIonRouter)
  {
    appIonRouter.push(path, 'forward', 'push');
  }
}

const IonRouterBridge: React.FC = () => {
  const ionRouter = useIonRouter();
  useEffect(() => {
    appIonRouter = ionRouter;
    return () => {
      appIonRouter = null;
    };
  }, [ionRouter]);
  return null;
};

let dangerZoneAlertIsOpen = false;

const App: React.FC = () => {

  var coordsList: any[] = [];
  var operatorsList: any[] = [];
  const serverUrl = `${BASE_URL}/poServer.php`;
  var watcherId = "";
  const { dispatch } = useContext(AuthContext)
  const [presentAlert] = useIonAlert();

  // create loacl storage object
  async function createLocalStorage() {
    await localStore.create();
  }

  /**
   * Ask for notification permission once, before location is requested.
   * Android allows only one permission dialog at a time; requesting push while
   * the location dialog is open fails silently ("Can request only one set...").
   */
  async function initPushNotifications() {
    if (Capacitor.getPlatform() === 'web')
    {
      return;
    }
    if (!Capacitor.isPluginAvailable('PushNotifications'))
    {
      console.log('App :: PushNotifications plugin not available');
      return;
    }

    try
    {
      let status = await FirebaseMessaging.checkPermissions();
      if (status.receive !== 'granted')
      {
        console.log('App :: asking Firebase Messaging / notification permission');
        status = await FirebaseMessaging.requestPermissions();
      }

      if (status.receive === 'granted')
      {
        console.log('App :: notification permission granted');
        await register();
      }
      else
      {
        console.log('App :: notification permission not granted:', status.receive);
      }
    }
    catch (e)
    {
      console.warn('App :: initPushNotifications failed', e);
    }
  }

  /**
   * iOS only: request the Critical Alerts authorization so the danger-zone siren
   * can play through the Ring/Silent switch, Do Not Disturb / Focus and volume 0.
   * On existing installs (already authorized for normal notifications) iOS shows
   * only the incremental "Allow Critical Alerts?" prompt. Requires the approved
   * com.apple.developer.usernotifications.critical-alerts entitlement.
   */
  async function requestCriticalAlertPermission() {
    if (Capacitor.getPlatform() !== 'ios')
    {
      return;
    }
    try
    {
      const result = await CriticalAlerts.requestCriticalPermissions();
      console.log('App :: critical alert permission:', result);
    }
    catch (e)
    {
      console.warn('App :: requestCriticalAlertPermission failed', e);
    }
  }

  // retrieve the device UUID
  const getDeviceInfo = async () => {
    await Device.getId().then((devId) => deviceId = devId.identifier);
  };

  function presentDangerZoneAlert(title: string, body?: string) {
    if (dangerZoneAlertIsOpen)
    {
      return;
    }
    dangerZoneAlertIsOpen = true;
    startAlarm();
    presentAlert({
      header: '⚠️ ZONA DI PERICOLO',
      message: body && body.trim() !== '' ? body : title,
      backdropDismiss: false,
      cssClass: 'critical-alert-dialog',
      onDidDismiss: () => {
        dangerZoneAlertIsOpen = false;
        stopAlarm();
        dangerZoneMonitor.noteAlertDismissed();
        // Android: stop the native siren; native monitor re-alerts in 30s if still inside.
        if (Capacitor.getPlatform() === 'android')
        {
          void CriticalAlerts.stop().catch(() => {});
        }
      },
      buttons: [
        {
          text: 'Ho capito',
          role: 'cancel',
        },
        {
          text: 'Vedi avvisi',
          handler: () => {
            navigateAppRoute('/navigation-warnings');
          },
        },
      ],
    });
  }

  function setupDangerZoneInAppAlerts(): () => void {
    if (Capacitor.getPlatform() === 'web')
    {
      return () => {};
    }

    const onDangerZoneEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ title?: string; body?: string }>).detail;
      presentDangerZoneAlert(detail?.title ?? 'Zona di pericolo', detail?.body);
    };
    window.addEventListener(DANGER_ZONE_ALERT_EVENT, onDangerZoneEvent);

    return () => {
      window.removeEventListener(DANGER_ZONE_ALERT_EVENT, onDangerZoneEvent);
    };
  }

  useEffect(() => {
    let removeDangerZoneAlertListener = () => {};
    let removeAppStateListener = () => {};
    (async () => {
      createLocalStorage(); // create local storage object for the whole app
      setUserInfo();
      getDeviceInfo();      // retrieve unique device ID for the whole app
      fetchOperatorsUuid(); // remove watcher if this user is a River Operator
      initAlarmAudioUnlock();
      removeDangerZoneAlertListener = setupDangerZoneInAppAlerts();
      removeAppStateListener = await setupKeepAwake();

      // Notifications first, then location — Android can show only one permission dialog at a time.
      await initPushNotifications();
      await requestCriticalAlertPermission();
      activateGeoWatcher();
      dangerZoneMonitor.start();
    })();
    return () => {
      removeDangerZoneAlertListener();
      removeAppStateListener();
      void KeepAwake.allowSleep().catch(() => {});
    };
  }, []); // empty dependency array to call userEffect() only once, onload.

  /**
   * Keep the screen on while the app is in the foreground (no auto-lock after
   * 10–20 minutes). Sleep is re-enabled when the app goes to the background.
   */
  async function setupKeepAwake(): Promise<() => void> {
    if (Capacitor.getPlatform() === 'web')
    {
      return () => {};
    }

    const enable = async () => {
      try
      {
        await KeepAwake.keepAwake();
      }
      catch (e)
      {
        console.warn('App :: KeepAwake.keepAwake failed', e);
      }
    };

    const disable = async () => {
      try
      {
        await KeepAwake.allowSleep();
      }
      catch (e)
      {
        console.warn('App :: KeepAwake.allowSleep failed', e);
      }
    };

    await enable();

    const handle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive)
      {
        void enable();
      }
      else
      {
        void disable();
      }
    });

    return () => {
      handle.remove();
    };
  }

  // fetch the list of uuids from memory - if none, fetch it from server - or return an empty array
  async function fetchOperatorsUuid() {
    if (operatorsList == null || operatorsList.length === 0) {
      // firstly try to get data from server
      fetchRemoteOperatorsUuid().then(operatorsList => {
        if (operatorsList) {
          // console.log('Informazioni sugli uuid dei meatori scaricate:',operatorsList);
          killGeoWatcher(operatorsList);
        }
        else {
          localStore.get("operatorsList")
            .then(
              (operatorsList) => {
                if (operatorsList != null) {
                  // console.log('Informazioni sugli uuid dei meatori da local storage ',operatorsList);
                  killGeoWatcher(operatorsList);
                }
                else {
                  console.log('Non sono presenti informazioni sugli uuid dei meatori in memoria');
                }
              },
              (error) => {
                console.log('Problema nella ricezione dei dati sugli uuid dei meatori', error);
              }
            );
        }
      });
    }
    else {
      // console.log('Informazioni sugli uuid dei meatori presenti in var:',operatorsList);
      killGeoWatcher(operatorsList);
    }
  }

  // fetch the list of uuids from server
  async function fetchRemoteOperatorsUuid(): Promise<[any] | null> {
    return new Promise(function (resolve, reject) {
      // load uuids from server
      const url = `${BASE_URL}/poServer.php?act=9&h=gkzLoFk0MN58TGqCJj`;
      fetch(url)
        .then(res => res.json())
        .then(
          (result) => {
            // console.log('Informazioni sugli uuid dei meatori scaricarte dal server.', result.content);
            localStore.set('operatorsList', result.content);
            resolve(result.content);
          },
          (error) => {
            // console.log('Problema nella ricezione dei dati sugli uuid dei meatori' ,error);
            resolve(null);
          }
        );
    });
  }

  async function activateGeoWatcher() 
  {
      // Skip if running in browser
      if (Capacitor.getPlatform() === 'web') {
        console.log('Background geolocation not available in browser');
        return;
      }

      BackgroundGeolocation!.addWatcher(
      {
        // On Android, a notification must be shown to continue receiving
        // location updates in the background. This option specifies the text of that notification.
        backgroundMessage: "Chiudi l'app per non essere tracciato.",
        // The title of the notification mentioned above. Defaults to "Using your location".
        backgroundTitle: "Tracciamento anonimo della posizione attivo.",
        // The minimum number of metres between subsequent locations. Defaults to 0.
        // Kept low so that, while the screen is locked, the native background
        // callback (which drives danger-zone detection) fires often enough to
        // catch zone entries before the user is well inside them.
        distanceFilter: 20
      },
      function callback(location: any, error: any) {
        if (error) {
          if (error.code === "NOT_AUTHORIZED") {
            if (window.confirm("L'App del Portolanod del Po ha bisogno della tua posizione per aiutarti a navigare lungo il Po ma devi dare il permesso all'app.\n\nVuoi andare alla schermata di configurazioni adesso?")) {
              // It can be useful to direct the user to their device's settings when location permissions have been denied.
              // The plugin provides the 'openSettings' method to do exactly this.
              BackgroundGeolocation!.openSettings();
            }
          }

          return console.error(error);
        }
        else {
          // Always keep the monitor up to date with the latest position
          dangerZoneMonitor.updatePosition(location.latitude, location.longitude);
        }

        if (relevantPoint([location.longitude, location.latitude])) // check wheter this point falls within relevant areas
        {
          const postData = {
            time: location.time,
            lat: location.latitude,
            lng: location.longitude,
            uuid: deviceId,
            h: "gkzLoFk0MN58TGqCJj",
            act: 8
          }

          fetch(serverUrl, {
            method: 'post',
            mode: 'cors', // this has to be 'cors'
            body: JSON.stringify(postData),
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })
            .then(res => res.json())
            .then(
              (response) => {
                // if the call went well, send also the coordinates that we have in memory
                sendStoredCoordinates();
              },
              (error) => {
                // console.log('Problema nell\'invio della posizione 1 - internet assente? ', error);
                // store in memory till internet connection won't be back
                storeCoordinatesInMemory(postData);
              })
            .catch(error => {
              // console.log('Problema nell\'invio della posizione 2 : ', error);
              // store in memory till internet connection won't be back
              storeCoordinatesInMemory(postData);
            })
        }
      })
      .then(function (watcher_id: any) {
        // When a watcher is no longer needed, it should be removed by calling 'removeWatcher' with an object containing its ID.
        watcherId = watcher_id; // store it to be used later if we need to kill the watcher
      });
  }

  // Determines if the given point is inside the shapeVertices polygon [point in polygon algorithm - ray cast algorithm]
  function relevantPoint(testPoint: number[]) {
    let result = false;
    let j = shapeVertices.length - 1;

    for (let i = 0; i < shapeVertices.length; i++) {
      if (shapeVertices[i][1] < testPoint[1] && shapeVertices[j][1] >= testPoint[1] || shapeVertices[j][1] < testPoint[1] && shapeVertices[i][1] >= testPoint[1]) {
        if (shapeVertices[i][0] + (testPoint[1] - shapeVertices[i][1]) / (shapeVertices[j][1] - shapeVertices[i][1]) * (shapeVertices[j][0] - shapeVertices[i][0]) < testPoint[0]) {
          result = !result;
        }
      }
      j = i;
    }
    return result;
  }

  function killGeoWatcher(operatorsList: any[]) 
  {
    // don't run on pc browser
    if (Capacitor.getPlatform() === 'web') 
    {
      return;
    }

    // console.log("Kill Geowatcher called - opertors: ",operatorsList.length);
    operatorsList.forEach(element => {
      //  console.log("Operator's uuid: ",element!.device_uuid);
      if (element!.device_uuid === deviceId) // got an operator!
      {
        BackgroundGeolocation!.removeWatcher({
          id: watcherId
        });
        // console.log("Geowatcher "+watcherId+" Killed");
        return;
      }
    });
  }

  // store user coordinates ind evice memory for future attempts to send them to server
  function storeCoordinatesInMemory(dataToStore: any) {
    // console.log('Coordinate presenti in memoria: ', coordsList.length);
    coordsList.push(dataToStore);
    // console.log('Aggiunte nuove coordinate: ',coordsList.length);
  }

  // attempt to send stored user coordinates to server
  function sendStoredCoordinates() {
    if (coordsList != null && coordsList.length > 0) // need to send stored coordinates to the server
    {
      // console.log('Tentativo di invio delle coordinate presenti in memoria - tot: ', coordsList.length);
      let postData = coordsList[0];
      fetch(serverUrl, {
        method: 'post',
        mode: 'cors', // this has to be 'cors'
        body: JSON.stringify(postData),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(
          (response) => {
            // if the data were sent correctly, remove them from the array
            coordsList.shift();
            // then go on with the next set of coordinates, it exists
            sendStoredCoordinates();
          },
          (error) => {
            console.log('Problema sul server nel tentativo di salvare coordinate memorizzate: ', error);
          })
        .catch(error => {
          console.log('Problema nell\'invio della posizione salvata sul server: ', error);
        });
    }
  }

  const setUserInfo = async () => {
    try {
      const user = await localStore.get('user')
      if (user) {
        const data = JSON.parse(user)
        await dispatch({ type: ActionType.SET_USER, payload: data })
      }
    } catch (error) {
      console.log(error)
    }
  }

  const register = async () => {
    console.log('App register:: starting register for platform: ',Capacitor.getPlatform());

    // Skip if running in browser
    if (Capacitor.getPlatform() === 'web')
    {
      console.log('App register:: Skipping Firebase Messaging on web');
      return;
    }

    // Permission already granted by initPushNotifications(); just fetch the FCM token.
    const { token } = await FirebaseMessaging.getToken();
    console.log('App register :: FCM token from Firebase (firebase plugin) :', token);
    setFirebaseTokent(token);

    // Optional: also register with PushNotifications plugin to get native behavior (e.g., onNotification)
    PushNotifications.register();  
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token) => {
      console.log('App register ::  PushNotifications plugin (generic plugin which stopped working on 1/5/2025 ???) :: APNs registration success (iOS only, not FCM):', token.value);
    });
  
    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error) => {
      alert('App register :: Push Notification registration error: ' + JSON.stringify(error));
    });
  
    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log("App register :: notification received :", notification);

      // Check if this is a server-sent critical alert (type_id = 4)
      const isCritical = notification.data && notification.data.alert_type === 'critical';

      if (isCritical) {
        // Critical alert: full-screen modal-style alert that requires explicit acknowledgement
        startAlarm();
        presentAlert({
          header: '⚠️ ' + (notification.title ?? 'ALLERTA CRITICA'),
          message: notification.body ?? '',
          backdropDismiss: false,
          cssClass: 'critical-alert-dialog',
          onDidDismiss: () => {
            stopAlarm();
          },
          buttons: [
            {
              text: 'Ho capito',
              role: 'cancel',
            },
            {
              text: 'Vedi avvisi',
              handler: () => {
                navigateAppRoute('/navigation-warnings');
              },
            },
          ],
        });
      } else {
        presentAlert({
          header: notification.title,
          message: notification.body,
          buttons: ['OK'],
          onDidDismiss: () => {
            navigateAppRoute('/navigation-warnings');
          }
        });
      }
    });
  
    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log("App register :: action performed:", notification);
      // store the route and notification data to navigate to after the app is ready in the local storage
      localStore.set('pendingPushRoute', '/navigation-warnings');
      localStore.set('lastNotificationData', JSON.stringify(notification.notification));
    });
  };

  CapacitorApp.addListener('appStateChange', ({ isActive, launchUrl }: { isActive: boolean, launchUrl?: string }) => {
    if (isActive) 
    {
      console.log("App :: appStateChange :: App became active, launchUrl:", launchUrl);

      // get route from local storage - that's the one set when the app was opened from a notification
      localStore.get('pendingPushRoute').then((pendingPushRoute: string | null) => {
        console.log("App :: redirect after tap on notification? pendingPushRoute:", pendingPushRoute);
        
        if (pendingPushRoute) 
        {
          console.log("App :: redirect after tap on notification? pendingPushRoute found, pushing to history");

          localStore.remove('pendingPushRoute');
          navigateAppRoute(pendingPushRoute);
        }
      });
    }
  });


  function setFirebaseTokent(fcmToken: string) {
    console.log("App :: setFirebaseTokent :: about to set firebase token");
    console.log("App :: setFirebaseTokent :: Raw FCM token:", fcmToken);
    console.log("App :: setFirebaseTokent :: Token length:", fcmToken.length);
    console.log("App :: setFirebaseTokent :: Platform:", Capacitor.getPlatform());
    
    const formData = new FormData()
    formData.append('fcm_token', fcmToken)
    formData.append('uuid', deviceId)

    const url = `${BASE_URL}/poServer.php?h=gkzLoFk0MN58TGqCJj&act=13`;
    fetch(url, {
      method: 'post',
      mode: 'cors', // this has to be 'cors'
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(res => res.json())
      .then((response) => {
        console.log("App :: got response after registering FCM token: ",response);
      })
      .catch(error => {
        console.log("App :: got error after trying to register FCM token: ",error);
      });
  }

  return (
    <LanguageProvider>
      <IonApp>
        <IonReactRouter>
          <IonRouterBridge />
          <IonSplitPane contentId="main">
            <Menu />
            <IonRouterOutlet id="main">
              <Route path="/" exact={true}>
                <Redirect to="/home" />
              </Route>
              <Route path="/home" exact={true}>
                <Home />
              </Route>
              <Route path="/docks" exact={true}>
                <Docks />
              </Route>
              <Route path="/docksMap" exact={true}>
                <DocksMap />
              </Route>
              <Route path="/docksMap/:type/:id" exact={true}>
                <DocksMap />
              </Route>
              <Route path="/dock/:id" exact={true}>
                <Dock />
              </Route>
              <Route path="/maps" exact={true}>
                <Maps />
              </Route>
              <Route path="/islands" exact={true}>
                <Islands />
              </Route>
              <Route path="/islandsMap" exact={true}>
                <IslandsMap />
              </Route>
              <Route path="/routes" exact={true}>
                <Routes />
              </Route>
              <Route path="/batimetric" exact={true}>
                <Batimetric />
              </Route>
              <Route path="/navigation-warnings" exact={true}>
                <NavigationWarnings />
              </Route>
              <Route path="/signs" exact={true}>
                <Signs />
              </Route>
              <Route path="/bridges" exact={true}>
                <Bridges />
              </Route>
              <Route path="/rulebook" exact={true}>
                <Rulebook />
              </Route>
              <Route path="/feedback" exact={true}>
                <Feedback />
              </Route>
              <Route path="/credits" exact={true}>
                <Credits />
              </Route>
              <Route path="/login" exact={true}>
                <Login />
              </Route>
              <Route path="/meatori" exact={true}>
                <Meatori />
              </Route>
            </IonRouterOutlet>
          </IonSplitPane>
        </IonReactRouter>
      </IonApp>
    </LanguageProvider>
  );
};

export default App;
export { localStore, deviceId, appVersion };

/*
https://ionicframework.com/docs/
https://ionicframework.com/docs/components
https://capacitorjs.com/docs/apis/geolocation#position

List of page indexes:
home: 0
docksMap: 1
docks: 2
dock: 3
maps: 4
islands: 5
islandsMap: 6
batimetric: 7
signs: 8
bridges: 9
rulebook: 10
feedback: 11
credits: 12
routes: 13 [not used]

*/
